import Button from 'components/ui/button';
import { BehaviorLabel } from 'utils/strings';

import { Recognition } from '../recognition';
import { COIN_TIERS } from '../recognition-points';
import { ColleagueAvatar } from './colleague';
import { COIN_ART } from './recognition-art';
import { RecognitionMessage } from './review-step';
import { ActionBar, HEADING_FOCUS_PROPS } from './step-layout';

/** Título das telas de resultado: recebe o foco quando a tela aparece (REC-52). */
function ResultHeading({ children, className }: { children: string; className: string }) {
  return (
    <h3 {...HEADING_FOCUS_PROPS} className={`m-0 font-display font-extrabold outline-none ${className}`}>
      {children}
    </h3>
  );
}

/** Enquanto o envio está em curso, no lugar da etapa (REC-46). */
export function SendingState() {
  return (
    <p
      role="status"
      className="m-0 py-10 text-center font-display text-lg font-bold text-brand-blue-dark motion-safe:animate-pulse"
    >
      Enviando reconhecimento...
    </p>
  );
}

interface RecognitionSentProps {
  /** Mensagem do servidor, ou a padrão de agradecimento. */
  serverMessage: string;
  /** O reconhecimento como foi enviado. */
  recognition: Recognition;
  onClose: () => void;
}

/** Reconhecimento enviado: o resumo do que foi mandado e o prazo de cancelamento (REC-48). */
export function RecognitionSent({ serverMessage, recognition, onClose }: RecognitionSentProps) {
  const { target, behavior, coin, points, message } = recognition;

  return (
    <div className="flex flex-col items-center gap-3.5 text-center">
      <ResultHeading className="text-[32px] leading-none text-brand-blue-dark">Uau!</ResultHeading>

      <p className="m-0 max-w-[430px] text-sm leading-[1.6] text-content [text-wrap:pretty]">{serverMessage}</p>

      <div className="flex w-full flex-col gap-3 rounded-jj-xl bg-brand-pink-light p-3.5 sm:p-[18px]">
        <div className="flex flex-col gap-3.5 rounded-jj-lg bg-brand-white px-4 py-[18px] text-left shadow-[0_2px_10px_rgba(17,24,39,.06)] sm:px-5">
          <div className="flex flex-wrap items-center gap-3">
            <img src={COIN_ART[coin]} alt="" className="h-11 w-11 shrink-0 object-contain" />
            <p className="m-0 flex flex-col gap-0.5">
              <span className="font-display text-[17px] font-extrabold leading-none text-brand-blue-dark">
                {COIN_TIERS[coin].label}
              </span>
              <span className="text-[11.5px] text-content-soft">enviado agora</span>
            </p>
            <span className="ml-auto whitespace-nowrap rounded-jj-pill bg-brand-yellow px-[15px] py-[7px] font-display text-[12.5px] font-bold text-brand-blue-dark">
              +{points} pontos
            </span>
          </div>

          <div aria-hidden="true" className="h-px bg-surface-200" />

          <p className="m-0 flex flex-wrap items-center gap-2.5">
            <ColleagueAvatar person={target} />
            <span className="screen-reader-text">Para </span>
            <span className="font-display text-sm font-bold text-content">{target.name}</span>
            <span className="screen-reader-text">, pelo comportamento </span>
            <span className="rounded-jj-pill bg-brand-purple-light px-3 py-[5px] text-[11px] font-semibold uppercase tracking-[.03em] text-content">
              {BehaviorLabel[behavior]}
            </span>
          </p>

          <RecognitionMessage message={message} className="border-l-[3px] border-brand-pink pl-3" />
        </div>

        <p className="m-0 text-xs text-brand-blue-dark">Você ainda pode cancelar este reconhecimento nos próximos 15 minutos.</p>
      </div>

      <ActionBar>
        <Button variant="solido-destaque" onClick={onClose}>
          Fechar
        </Button>
      </ActionBar>
    </div>
  );
}

interface RecognitionFailedProps {
  /** Mensagem do servidor, ou a padrão de erro. */
  serverMessage: string;
  /** Sem ele, a recusa é definitiva e a tela não oferece nova tentativa (REC-51). */
  onRetry?: () => void;
}

/** Envio recusado ou falho (REC-50). */
export function RecognitionFailed({ serverMessage, onRetry }: RecognitionFailedProps) {
  return (
    <div className="flex flex-col items-center gap-3.5 text-center">
      <ResultHeading className="text-2xl leading-tight text-brand-blue-dark">
        Não foi possível enviar o reconhecimento!
      </ResultHeading>

      <p className="m-0 max-w-[430px] text-sm leading-[1.6] text-content [text-wrap:pretty]">{serverMessage}</p>

      {onRetry && (
        <Button variant="solido-destaque" onClick={onRetry}>
          Tentar novamente
        </Button>
      )}
    </div>
  );
}
